/**
 * Enough of PostgREST to answer the app's reads from in-memory rows: the
 * filters (`eq`, `neq`, `gt`, `gte`, `lt`, `lte`, `is`, `in`, `like`, `ilike`,
 * `not.` and the `or=` / `and=` logic trees), `order` with several keys,
 * `offset` / `limit`, and filters on an embedded resource (`transactions.date`).
 *
 * Used only by the sample-data mode (`./backend.ts`). Pure, no Supabase import:
 * tested in `src/lib/demo/__tests__/postgrest.test.ts`.
 */

export type Row = Record<string, unknown>;

/** Query parameters that are not column filters. */
const RESERVED = new Set(["select", "order", "limit", "offset", "on_conflict", "columns"]);

/** `rel.col` reads a column of an embedded row (`transactions!inner(date)`). */
function fieldValue(row: Row, key: string): unknown {
  const dot = key.indexOf(".");
  if (dot === -1) return row[key];
  const embedded = row[key.slice(0, dot)];
  if (!embedded || typeof embedded !== "object") return undefined;
  return fieldValue(embedded as Row, key.slice(dot + 1));
}

/** A quoted PostgREST value (`"Alza, a.s."`) back to its text. */
function unquote(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1).replace(/\\(.)/g, "$1");
  }
  return value;
}

/** Splits on top-level commas, outside parentheses and double quotes. */
export function splitTopLevel(text: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let quoted = false;
  let current = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      current += ch;
      if (ch === "\\" && i + 1 < text.length) current += text[++i];
      else if (ch === '"') quoted = false;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === "(") depth++;
    else if (ch === ")") depth--;
    if (ch === "," && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current) parts.push(current);
  return parts;
}

function likeToRegExp(pattern: string, caseInsensitive: boolean): RegExp {
  const body = pattern
    .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
    .replace(/[*%]/g, ".*")
    .replace(/_/g, ".");
  return new RegExp(`^${body}$`, caseInsensitive ? "is" : "s");
}

function compare(value: unknown, arg: string): number {
  if (typeof value === "number") return value - Number(arg);
  const s = String(value);
  return s < arg ? -1 : s > arg ? 1 : 0;
}

/** One `op.value` test (already split from the column name), `not.` included. */
export function testOperator(value: unknown, expression: string): boolean {
  let expr = expression;
  let negate = false;
  if (expr.startsWith("not.")) {
    negate = true;
    expr = expr.slice(4);
  }
  const dot = expr.indexOf(".");
  const op = dot === -1 ? expr : expr.slice(0, dot);
  const raw = dot === -1 ? "" : expr.slice(dot + 1);
  const missing = value === null || value === undefined;
  let ok: boolean;
  switch (op) {
    case "eq":
      ok = !missing && String(value) === unquote(raw);
      break;
    case "neq":
      ok = !missing && String(value) !== unquote(raw);
      break;
    case "gt":
      ok = !missing && compare(value, unquote(raw)) > 0;
      break;
    case "gte":
      ok = !missing && compare(value, unquote(raw)) >= 0;
      break;
    case "lt":
      ok = !missing && compare(value, unquote(raw)) < 0;
      break;
    case "lte":
      ok = !missing && compare(value, unquote(raw)) <= 0;
      break;
    case "is":
      if (raw === "null") ok = missing;
      else if (raw === "true") ok = value === true;
      else if (raw === "false") ok = value === false;
      else ok = false;
      break;
    case "in": {
      const list = splitTopLevel(raw.replace(/^\(/, "").replace(/\)$/, "")).map(unquote);
      ok = !missing && list.includes(String(value));
      break;
    }
    case "like":
    case "ilike":
      ok = !missing && likeToRegExp(unquote(raw), op === "ilike").test(String(value));
      break;
    default:
      // Full-text search and other operators the app does not use on these tables.
      ok = true;
  }
  return negate ? !ok : ok;
}

/** A logic tree's body, `a.eq.1,and(b.gte.2,b.lte.3)`, against one row. */
function testLogic(row: Row, kind: "or" | "and", body: string): boolean {
  const results = splitTopLevel(body).map((part) => testCondition(row, part));
  return kind === "or" ? results.some(Boolean) : results.every(Boolean);
}

/** One condition inside a logic tree: `col.op.value`, `or(…)`, `and(…)`, `not.or(…)`. */
function testCondition(row: Row, condition: string): boolean {
  const tree = condition.match(/^(not\.)?(or|and)\((.*)\)$/s);
  if (tree) {
    const ok = testLogic(row, tree[2] as "or" | "and", tree[3]);
    return tree[1] ? !ok : ok;
  }
  // The column is everything before the operator: `transactions.date.gte.2026-01-01`.
  const match = condition.match(
    /^(.+?)\.((?:not\.)?(?:eq|neq|gt|gte|lt|lte|is|in|like|ilike|fts|plfts|phfts|wfts|cs|cd)\..*)$/s
  );
  if (!match) return true;
  return testOperator(fieldValue(row, match[1]), match[2]);
}

/** Keeps the rows that pass every filter in the query string. */
export function filterRows(rows: readonly Row[], params: URLSearchParams): Row[] {
  let out = [...rows];
  params.forEach((value, key) => {
    if (RESERVED.has(key)) return;
    const tree = key.match(/^(not\.)?(or|and)$/);
    if (tree) {
      const body = value.replace(/^\(/, "").replace(/\)$/, "");
      out = out.filter((row) => {
        const ok = testLogic(row, tree[2] as "or" | "and", body);
        return tree[1] ? !ok : ok;
      });
      return;
    }
    out = out.filter((row) => testOperator(fieldValue(row, key), value));
  });
  return out;
}

/** `order=date.desc.nullslast,id.desc`: Postgres puts nulls last ascending, first descending. */
export function orderRows(rows: readonly Row[], order: string | null): Row[] {
  if (!order) return [...rows];
  const keys = order.split(",").map((part) => {
    const [column, ...flags] = part.split(".");
    const descending = flags.includes("desc");
    const nullsFirst = flags.includes("nullsfirst")
      ? true
      : flags.includes("nullslast")
        ? false
        : descending;
    return { column, descending, nullsFirst };
  });
  return [...rows].sort((a, b) => {
    for (const key of keys) {
      const x = fieldValue(a, key.column);
      const y = fieldValue(b, key.column);
      const xNull = x === null || x === undefined;
      const yNull = y === null || y === undefined;
      if (xNull || yNull) {
        if (xNull && yNull) continue;
        return xNull === key.nullsFirst ? -1 : 1;
      }
      const diff =
        typeof x === "number" && typeof y === "number"
          ? x - y
          : String(x) < String(y)
            ? -1
            : String(x) > String(y)
              ? 1
              : 0;
      if (diff !== 0) return key.descending ? -diff : diff;
    }
    return 0;
  });
}

export interface QueryResult {
  rows: Row[];
  /** Matching rows before paging, for `Content-Range`. */
  total: number;
  offset: number;
}

/** Filters, orders and pages rows the way PostgREST would for this query string. */
export function runQuery(rows: readonly Row[], params: URLSearchParams): QueryResult {
  const ordered = orderRows(filterRows(rows, params), params.get("order"));
  const offset = Number(params.get("offset") || 0);
  const limit = params.get("limit");
  const page =
    limit !== null ? ordered.slice(offset, offset + Number(limit)) : ordered.slice(offset);
  return { rows: page, total: ordered.length, offset };
}
